import React, { useState } from "react";
import client from "@/api/client";
import { Sparkles, TrendingUp, AlertTriangle, CheckCircle2, Zap,
         RefreshCw, ChevronDown, ChevronUp, ArrowRight, Info, Shield, Hash } from "lucide-react";

const SEVERITY_CONFIG = {
  high:   { class: "bg-red-50 border-red-200 text-red-700",   dot: "bg-red-500" },
  medium: { class: "bg-amber-50 border-amber-200 text-amber-700", dot: "bg-amber-500" },
  low:    { class: "bg-slate-50 border-slate-200 text-slate-600",  dot: "bg-slate-400" }
};
const IMPACT_CONFIG = {
  High:   { class: "bg-emerald-50 text-emerald-700" },
  Medium: { class: "bg-blue-50 text-blue-700" },
  Low:    { class: "bg-slate-50 text-slate-600" }
};
const PRIORITY_CONFIG = {
  immediate:    { class: "bg-red-50 text-red-600",   label: "Immediate" },
  "short-term": { class: "bg-amber-50 text-amber-700", label: "Short-term" },
  "medium-term":{ class: "bg-blue-50 text-blue-700",   label: "Medium-term" }
};

function ProvenancePanel({ insights }) {
  if (!insights?.provenance) return null;
  return (
    <div className="px-5 pb-4">
      <div className="p-3 bg-slate-50 border border-slate-200 rounded-xl">
        <div className="flex items-start gap-2">
          <Hash size={12} className="text-slate-400 mt-0.5 flex-shrink-0" />
          <div>
            <p className="text-xs font-semibold text-slate-600 mb-1">Data Provenance</p>
            <p className="text-xs font-mono text-slate-500 leading-relaxed">{insights.provenance}</p>
          </div>
        </div>
      </div>
    </div>
  );
}

function DisclaimerPanel({ disclaimer }) {
  return (
    <div className="px-5 pb-4">
      <div className="p-3 bg-blue-50 border border-blue-200 rounded-xl">
        <div className="flex items-start gap-2">
          <Info size={12} className="text-blue-600 mt-0.5 flex-shrink-0" />
          <div>
            <p className="text-xs font-semibold text-blue-800 mb-0.5">AI Analysis Disclaimer</p>
            <p className="text-xs text-blue-700 leading-relaxed">{disclaimer}</p>
            <p className="text-xs text-blue-600 mt-1.5 font-medium">
              This analysis does not constitute third-party verification, financial advice, or regulatory compliance confirmation. All calculations are governed by the selected methodology and factor versions.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function ExecutiveInsights({ assessmentId, scorecard }) {
  const [insights, setInsights] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [expandedSection, setExpandedSection] = useState("summary");

  const generate = async (forceRefresh = false) => {
    setLoading(true);
    setError(null);
    try {
      const res = await client.post(`/insights/assessments/${assessmentId}/executive`,
        { force_refresh: forceRefresh });
      // Inject data provenance from scorecard if available
      const result = res.data;
      if (scorecard) {
        result.provenance = [
          `Assessment ID: ${assessmentId}`,
          `Calculation Hash: ${scorecard.calculation_hash || "N/A"}`,
          `Methodology: ${scorecard.methodology_name || "GHG Protocol"} v${scorecard.methodology_version || "2.0"}`,
          `Activities: ${scorecard.activity_count || "—"} records | Factors: ${scorecard.factor_count || "—"} used`,
          `Total: ${scorecard.total_emissions_tco2e?.toFixed(2) || "0"} tCO2e | Confidence: ${scorecard.data_confidence?.toFixed(1) || "0"}%`,
        ].join("  |  ");
      }
      setInsights(result);
      setExpandedSection("summary");
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to generate insights");
    }
    setLoading(false);
  };

  if (!insights && !loading) {
    return (
      <div className="bg-white border border-slate-200 rounded-xl p-6" data-testid="executive-insights-empty">
        <div className="flex items-center justify-between mb-2">
          <div>
            <h3 className="text-sm font-semibold text-[#0F172A] flex items-center gap-2">
              <Sparkles size={15} className="text-[#059669]" /> AI Executive Intelligence
            </h3>
            <p className="text-xs text-slate-400 mt-0.5">
              Risks, opportunities, and recommended actions — Claude Sonnet
            </p>
          </div>
        </div>
        <p className="text-xs text-slate-400 mb-4 p-3 bg-blue-50 border border-blue-100 rounded-lg">
          <strong className="text-blue-700">Disclaimer:</strong> AI insights are generated from your submitted data only.
          They do not constitute third-party verification or financial advice.
        </p>
        <button onClick={() => generate(false)} data-testid="generate-insights-btn"
          className="w-full py-3 border-2 border-dashed border-slate-200 text-sm text-slate-500 rounded-xl hover:border-[#059669] hover:text-[#059669] hover:bg-emerald-50/30 transition-all flex items-center justify-center gap-2">
          <Sparkles size={14} /> Generate Executive Intelligence Report
        </button>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="bg-white border border-slate-200 rounded-xl p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-4 h-4 border-2 border-[#059669] border-t-transparent rounded-full animate-spin" />
          <p className="text-sm text-slate-500">Analysing assessment data with Claude Sonnet...</p>
        </div>
        <div className="space-y-2">
          {[...Array(5)].map((_, i) => (
            <div key={i} className="h-3 bg-slate-100 rounded animate-pulse" style={{ width: `${85 - i*8}%` }} />
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white border border-slate-200 rounded-xl p-5">
        <div className="flex items-start gap-3 text-red-600">
          <AlertTriangle size={15} className="mt-0.5 flex-shrink-0" />
          <div>
            <p className="text-sm font-medium">Analysis unavailable</p>
            <p className="text-xs text-slate-400 mt-1">{error}</p>
            <button onClick={() => generate(true)} className="mt-2 text-xs text-[#059669] hover:underline">Try again</button>
          </div>
        </div>
      </div>
    );
  }

  const Section = ({ id, title, icon: Icon, children, count }) => (
    <div className="border border-slate-100 rounded-xl overflow-hidden">
      <button
        onClick={() => setExpandedSection(expandedSection === id ? null : id)}
        className="w-full flex items-center justify-between px-5 py-3 hover:bg-slate-50 transition-colors"
      >
        <div className="flex items-center gap-2">
          <Icon size={14} className="text-[#059669]" />
          <span className="text-sm font-semibold text-[#0F172A]">{title}</span>
          {count && <span className="text-xs px-2 py-0.5 bg-slate-100 text-slate-500 rounded-full">{count}</span>}
        </div>
        {expandedSection === id ? <ChevronUp size={14} className="text-slate-400" /> : <ChevronDown size={14} className="text-slate-400" />}
      </button>
      {expandedSection === id && <div className="px-5 pb-4 space-y-3">{children}</div>}
    </div>
  );

  return (
    <div className="bg-white border border-slate-200 rounded-xl overflow-hidden" data-testid="executive-insights">
      {/* Header */}
      <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Sparkles size={15} className="text-[#059669]" />
          <span className="text-sm font-semibold text-[#0F172A]">AI Executive Intelligence</span>
          {insights.cached && (
            <span className="text-xs text-slate-400 px-2 py-0.5 bg-slate-50 border border-slate-200 rounded">Cached</span>
          )}
        </div>
        <button onClick={() => generate(true)} disabled={loading}
          className="inline-flex items-center gap-1 text-xs text-slate-400 hover:text-slate-600 px-2 py-1 rounded hover:bg-slate-50 transition-colors">
          <RefreshCw size={11} className={loading ? "animate-spin" : ""} /> Regenerate
        </button>
      </div>

      <div className="p-5 space-y-3">
        {/* Summary */}
        <Section id="summary" title="Executive Summary" icon={Zap}>
          <p className="text-sm text-slate-700 leading-relaxed">{insights.executive_summary}</p>
          {insights.headline_metrics?.length > 0 && (
            <div className="grid grid-cols-3 gap-4 mt-3">
              {insights.headline_metrics.map((metric, i) => (
                <div key={i} className="p-3 bg-slate-50 border border-slate-100 rounded-lg">
                  <p className="text-xs text-slate-400 mb-0.5">{metric.label}</p>
                  <p className="text-sm font-bold text-[#0F172A]">{metric.value}</p>
                  <p className="text-xs text-slate-400 mt-0.5 leading-tight">{metric.context}</p>
                </div>
              ))}
            </div>
          )}
        </Section>

        {/* Key Findings */}
        {insights.key_findings?.length > 0 && (
          <Section id="findings" title="Key Findings" icon={TrendingUp} count={insights.key_findings.length}>
            {insights.key_findings.map((f, i) => (
              <div key={i} className={`p-3 rounded-lg border ${f.severity === "positive" ? "bg-emerald-50 border-emerald-100" : f.severity === "concern" ? "bg-amber-50 border-amber-100" : "bg-slate-50 border-slate-100"}`}>
                <p className="text-sm font-semibold text-[#0F172A] mb-1">{f.title}</p>
                <p className="text-xs text-slate-600 leading-relaxed">{f.detail}</p>
              </div>
            ))}
          </Section>
        )}

        {/* Risks */}
        {insights.risks?.length > 0 && (
          <Section id="risks" title="Material Risks" icon={AlertTriangle} count={insights.risks.length}>
            {insights.risks.map((r, i) => {
              const sc = SEVERITY_CONFIG[r.severity] || SEVERITY_CONFIG.medium;
              return (
                <div key={i} className={`p-3 rounded-lg border ${sc.class}`}>
                  <div className="flex items-center gap-2 mb-1">
                    <div className={`w-2 h-2 rounded-full ${sc.dot} flex-shrink-0`} />
                    <p className="text-sm font-semibold flex-1">{r.title}</p>
                    <span className="text-xs capitalize font-medium">{r.severity}</span>
                  </div>
                  <p className="text-xs leading-relaxed pl-4">{r.description}</p>
                </div>
              );
            })}
          </Section>
        )}

        {/* Opportunities */}
        {insights.opportunities?.length > 0 && (
          <Section id="opportunities" title="Strategic Opportunities" icon={CheckCircle2} count={insights.opportunities.length}>
            {insights.opportunities.map((o, i) => {
              const ic = IMPACT_CONFIG[o.impact] || IMPACT_CONFIG.Medium;
              return (
                <div key={i} className="p-3 bg-emerald-50 border border-emerald-100 rounded-lg">
                  <div className="flex items-center gap-2 mb-1">
                    <p className="text-sm font-semibold text-[#0F172A] flex-1">{o.title}</p>
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${ic.class}`}>{o.impact} Impact</span>
                  </div>
                  <p className="text-xs text-slate-600 leading-relaxed">{o.description}</p>
                </div>
              );
            })}
          </Section>
        )}

        {/* Recommended Actions */}
        {insights.recommended_actions?.length > 0 && (
          <Section id="actions" title="Recommended Actions" icon={ArrowRight} count={insights.recommended_actions.length}>
            {insights.recommended_actions.map((a, i) => {
              const pc = PRIORITY_CONFIG[a.priority] || PRIORITY_CONFIG["short-term"];
              return (
                <div key={i} className="flex items-start gap-3 p-3 border border-slate-100 rounded-lg hover:bg-slate-50">
                  <div className="w-6 h-6 bg-[#0F172A] text-white rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 mt-0.5">{i + 1}</div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-[#0F172A]">{a.action}</p>
                    <div className="flex items-center gap-2 mt-1 flex-wrap">
                      <span className="text-xs text-slate-400">{a.timeline}</span>
                      <span className="text-slate-300">·</span>
                      <span className="text-xs text-[#059669] font-medium">{a.estimated_benefit}</span>
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${pc.class}`}>{pc.label}</span>
                    </div>
                  </div>
                </div>
              );
            })}
          </Section>
        )}

        {insights.compliance_notes && (
          <div className="px-4 py-3 bg-blue-50 border border-blue-100 rounded-xl">
            <div className="flex items-start gap-2">
              <Shield size={12} className="text-blue-600 mt-0.5 flex-shrink-0" />
              <p className="text-xs text-blue-700 leading-relaxed">
                <span className="font-semibold">Compliance Note: </span>{insights.compliance_notes}
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Data Provenance */}
      <ProvenancePanel insights={insights} />

      {/* Disclaimer */}
      <DisclaimerPanel disclaimer={insights.disclaimer} />
    </div>
  );
}
